import * as React from "react";
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { cn } from "@/shadcn/lib/utils";

import { Button } from "@/shadcn/components/ui/button";
import { XIcon } from "lucide-react";

// playerdex · registry v1 · shadcn@4.21.0

const Sheet = React.forwardRef(function Sheet({ ...props }, ref) {
  return <SheetPrimitive.Root data-slot="sheet" ref={ref} {...props} />;
});

const SheetTrigger = React.forwardRef(function SheetTrigger({ ...props }, ref) {
  return (
    <SheetPrimitive.Trigger data-slot="sheet-trigger" ref={ref} {...props} />
  );
});

const SheetClose = React.forwardRef(function SheetClose({ ...props }, ref) {
  return <SheetPrimitive.Close data-slot="sheet-close" ref={ref} {...props} />;
});

const SheetPortal = React.forwardRef(function SheetPortal({ ...props }, ref) {
  return (
    <SheetPrimitive.Portal
      data-slot="sheet-portal"
      ref={ref}
      {...props}
    />
  );
});

const SheetOverlay = React.forwardRef(function SheetOverlay(
  { className, ...props },
  ref
) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "bg-black/30 supports-backdrop-filter:backdrop-blur-sm fixed inset-0 z-[1300] transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const SheetContent = React.forwardRef(function SheetContent(
  {
    className,
    children,
    side = "right",
    showCloseButton = true,
    closeLabel = "Close",
    closeDisabled = false,
    ...props
  },
  ref
) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "bg-popover text-popover-foreground fixed z-[1300] flex flex-col has-[>[data-slot=sheet-close]]:[&_[data-slot=sheet-title]]:pr-8 bg-clip-padding text-sm shadow-xl transition duration-200 ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem]",
          className
        )}
        ref={ref}
        {...props}
      >
        {/* First in the DOM so it is the first tab stop. */}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            aria-label={closeLabel}
            disabled={closeDisabled}
            render={
              <Button
                variant="ghost"
                className="absolute top-4 right-4 bg-secondary"
                size="icon-sm"
              />
            }
          >
            <XIcon aria-hidden="true" />
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
});

const SheetHeader = React.forwardRef(function SheetHeader(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("gap-1.5 p-6 flex flex-col", className)}
      ref={ref}
      {...props}
    />
  );
});

const SheetFooter = React.forwardRef(function SheetFooter(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="sheet-footer"
      // Right padding keeps buttons clear of the fixed Intercom launcher.
      // Phones get a full-width sheet, so the padding stays even there.
      className={cn("gap-2 p-6 sm:pr-20 mt-auto flex flex-col", className)}
      ref={ref}
      {...props}
    />
  );
});

const SheetTitle = React.forwardRef(function SheetTitle(
  { className, ...props },
  ref
) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground text-base font-medium", className)}
      ref={ref}
      {...props}
    />
  );
});

const SheetDescription = React.forwardRef(function SheetDescription(
  { className, ...props },
  ref
) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      ref={ref}
      {...props}
    />
  );
});

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
